import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function Contact() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;

    setFormState('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch('https://readdy.ai/api/form/d6tdlfoq778icnc8un70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className="min-h-screen bg-brand-black font-body">
      <Navbar />

      {/* Header */}
      <section className="relative w-full pt-32 pb-20 px-6 lg:px-12 bg-brand-charcoal">
        <div className="w-full max-w-7xl mx-auto">
          <p className="font-heading text-brand-green-accent text-xs tracking-[0.4em] uppercase mb-3">Reach Out</p>
          <h1 className="font-heading font-800 text-white text-6xl md:text-7xl tracking-widest uppercase">
            Contact Us
          </h1>
          <div className="w-12 h-0.5 bg-brand-green-accent mt-5" />
        </div>
      </section>

      <main className="w-full px-6 lg:px-12 py-20">
        <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">

          {/* Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div>
              <h2 className="font-heading font-800 text-white text-3xl tracking-widest uppercase mb-4">
                Let's Discuss Your Project
              </h2>
              <div className="w-8 h-0.5 bg-brand-green-accent mb-5" />
              <p className="font-body text-sm text-gray-400 leading-relaxed">
                We're based in Northeast Tennessee and serve customers nationwide. Contact us to discuss your project location, timeline, and scope. One call covers everything.
              </p>
            </div>

            {/* Phone */}
            <div className="bg-brand-charcoal border border-brand-charcoal-mid p-6 flex items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-brand-green/20 border border-brand-green-mid rounded-sm shrink-0">
                <i className="ri-phone-line text-brand-green-accent text-2xl" />
              </div>
              <div>
                <p className="font-heading text-xs tracking-widest uppercase text-gray-500 mb-1">Phone</p>
                <a
                  href="tel:+14236780298"
                  className="font-heading font-700 text-white text-2xl tracking-wider hover:text-brand-green-accent transition-colors cursor-pointer"
                >
                  (423) 678-0298
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="bg-brand-charcoal border border-brand-charcoal-mid p-6 flex items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-brand-green/20 border border-brand-green-mid rounded-sm shrink-0">
                <i className="ri-mail-line text-brand-green-accent text-2xl" />
              </div>
              <div>
                <p className="font-heading text-xs tracking-widest uppercase text-gray-500 mb-1">Email</p>
                <a
                  href="mailto:projects@holstonlandmgmt.com"
                  className="font-heading font-700 text-white text-lg tracking-wider hover:text-brand-green-accent transition-colors cursor-pointer break-all"
                >
                  projects@holstonlandmgmt.com
                </a>
              </div>
            </div>

            {/* Location */}
            <div className="bg-brand-charcoal border border-brand-charcoal-mid p-6 flex items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-brand-green/20 border border-brand-green-mid rounded-sm shrink-0">
                <i className="ri-map-pin-line text-brand-green-accent text-2xl" />
              </div>
              <div>
                <p className="font-heading text-xs tracking-widest uppercase text-gray-500 mb-1">Location</p>
                <p className="font-heading font-700 text-white text-lg tracking-wider">Northeast Tennessee</p>
                <p className="font-body text-xs text-gray-400 mt-1">Serving Customers Nationwide</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-brand-charcoal border border-brand-charcoal-mid p-10">
              <h3 className="font-heading font-700 text-white text-2xl tracking-widest uppercase mb-3">
                Request a Quote
              </h3>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-brand-green-accent text-xs" />
                </div>
                <p className="font-heading text-xs tracking-widest uppercase text-gray-400">Most quotes can be provided within 24–48 hours</p>
              </div>

              {formState === 'success' ? (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-brand-green/20 border border-brand-green-mid rounded-sm mx-auto mb-6">
                    <i className="ri-check-line text-brand-green-accent text-3xl" />
                  </div>
                  <h4 className="font-heading font-700 text-white text-2xl tracking-widest uppercase mb-3">
                    Message Sent!
                  </h4>
                  <p className="font-body text-sm text-gray-400">
                    Thanks for reaching out. We'll be in touch shortly to discuss your project.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="holston-contact-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div>
                      <label className="font-heading text-xs tracking-widest uppercase text-gray-400 block mb-2">
                        Full Name <span className="text-brand-green-accent">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Smith"
                        className="w-full bg-brand-black border border-brand-charcoal-mid focus:border-brand-green-accent text-white text-sm font-body px-4 py-3 outline-none transition-colors placeholder:text-gray-600"
                      />
                    </div>
                    {/* Phone */}
                    <div>
                      <label className="font-heading text-xs tracking-widest uppercase text-gray-400 block mb-2">
                        Phone Number <span className="text-brand-green-accent">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(423) 555-0100"
                        className="w-full bg-brand-black border border-brand-charcoal-mid focus:border-brand-green-accent text-white text-sm font-body px-4 py-3 outline-none transition-colors placeholder:text-gray-600"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="font-heading text-xs tracking-widest uppercase text-gray-400 block mb-2">
                      Email Address <span className="text-brand-green-accent">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full bg-brand-black border border-brand-charcoal-mid focus:border-brand-green-accent text-white text-sm font-body px-4 py-3 outline-none transition-colors placeholder:text-gray-600"
                    />
                  </div>

                  {/* Project Description */}
                  <div>
                    <label className="font-heading text-xs tracking-widest uppercase text-gray-400 block mb-2">
                      Project Description <span className="text-brand-green-accent">*</span>
                    </label>
                    <textarea
                      name="project_description"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Describe your project — location, acreage, services needed, timeline..."
                      className="w-full bg-brand-black border border-brand-charcoal-mid focus:border-brand-green-accent text-white text-sm font-body px-4 py-3 outline-none transition-colors placeholder:text-gray-600 resize-none"
                      onChange={(e) => setCharCount(e.target.value.length)}
                    />
                    <p className={`font-body text-xs mt-1 text-right ${charCount > 480 ? 'text-yellow-500' : 'text-gray-600'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {formState === 'error' && (
                    <div className="bg-red-900/20 border border-red-800 px-4 py-3">
                      <p className="font-body text-sm text-red-400">Something went wrong. Please try again or call us directly.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting' || charCount > 500}
                    className="w-full py-4 bg-brand-green hover:bg-brand-green-mid disabled:opacity-60 font-heading font-700 text-white text-base tracking-widest uppercase cursor-pointer whitespace-nowrap transition-colors rounded-sm"
                  >
                    {formState === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>


                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
